/**
 * Shared design tokens — the single source for Tailwind class strings that were
 * previously duplicated as `const wrap = "..."` in 6 files.
 *
 * Widths come from the two containers declared in `@theme` (styles.css):
 * `max-w-measure` is the reading width and `max-w-page` the outer wrap. Spell
 * those rather than `max-w-[34rem]`, so changing the measure is one edit.
 *
 * Tailwind JIT scans this file (static string literals), so do not build
 * classes via runtime interpolation like `bg-${color}`.
 */

export const wrap = "w-full max-w-page mx-auto px-[clamp(1rem,5vw,2rem)]";

export const band = "py-[clamp(2.6rem,7vw,4.4rem)] border-t border-line first:border-t-0";
/**
 * The alternating grounds. Both carry `grain` because both paint an opaque
 * background — without it they cover the paper texture `.page` lays down and
 * the site reads as textured on every other band only.
 */
export const bandAlt = "bg-faint grain";
export const bandHero =
  "bg-faint grain pt-[clamp(3rem,9vw,6rem)] pb-[clamp(2.6rem,7vw,4.6rem)] border-t border-line first:border-t-0";

export const eyebrow =
  "font-sans leading-[1.65] text-[0.74rem] uppercase tracking-[0.16em] font-bold text-rust mb-[0.6rem]";

export const lede = "text-[1.16rem] text-muted max-w-measure";
export const ledeLarge = "text-[clamp(1.08rem,2.4vw,1.25rem)] text-muted max-w-measure";

export const prose = "max-w-measure [&>:last-child]:mb-0";

export const sectionHead =
  "mt-0 text-[clamp(1.7rem,4.5vw,2.3rem)] font-display font-bold leading-[1.15]";

export const split = "grid gap-[clamp(1.6rem,4vw,3rem)] split:grid-cols-2";

export const hero =
  "grid gap-[clamp(1.6rem,5vw,3rem)] items-center hero:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]";
export const heroEmblem =
  "w-[min(100%,300px)] h-auto justify-self-center max-hero:w-[min(62%,220px)]";
export const heroEmblemSmall = "w-[min(100%,210px)] h-auto justify-self-center max-hero:hidden";

export const heroActions = "flex gap-[0.7rem] flex-wrap mt-[1.6rem]";

export const promises = "grid gap-[clamp(1.4rem,3.5vw,2.4rem)] mt-[1.8rem] split:grid-cols-3";

export const btn =
  "inline-block bg-rust text-panel border border-rust rounded-box px-[1.25rem] py-[0.68rem] font-bold text-label uppercase tracking-[0.11em] no-underline cursor-pointer min-h-[44px] leading-[1.7] hover:bg-rust-deep hover:border-rust-deep hover:text-panel disabled:opacity-40 disabled:cursor-default";

/** Ghost variant — same shape as `btn` but transparent, bordered in muted. */
export const btnGhost =
  "inline-block bg-transparent text-ink border border-muted rounded-box px-[1.25rem] py-[0.68rem] font-bold text-label uppercase tracking-[0.11em] no-underline cursor-pointer min-h-[44px] leading-[1.7] hover:border-rust hover:bg-transparent hover:text-rust";

/**
 * The bordered note/callout (used for archive notice, book promises, etc.).
 * `grain` because `bg-panel` is opaque, same reason as the bands.
 */
export const note =
  "border border-line border-l-[3px] border-l-rust rounded-box bg-panel grain py-[1.05rem] px-[1.2rem] text-muted text-minor max-w-measure [&_strong]:text-ink [&>:last-child]:mb-0";

/** Tick-list item (used for book contents). */
export const contentsItem =
  "border-t border-line py-[0.7rem] pl-[1.6rem] relative text-minor before:content-[''] before:absolute before:left-[0.15rem] before:top-[1.15rem] before:w-2 before:h-[0.28rem] before:border-l-2 before:border-b-2 before:border-rust before:rotate-[-45deg] before:block";

/**
 * A cover, at the two places one appears. Portrait, because every book is
 * 8.5×11 and a fixed aspect keeps a row from jumping while the image loads.
 */
export const coverFrame =
  "block w-full h-auto aspect-[85/110] object-cover bg-faint border border-line rounded-[2px] shadow-[0_1px_3px_rgba(0,0,0,0.12)]";
